
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PhoneAnimation from '../../components/feature/PhoneAnimation';

const PartnershipsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-navy via-navy/95 to-navy/90">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          
          {/* Animated background elements */}
          <div className="absolute top-20 left-10 opacity-20">
            <PhoneAnimation variant="floating" size="sm" />
          </div>
          <div className="absolute top-40 right-20 opacity-20">
            <PhoneAnimation variant="pulse" size="sm" />
          </div>
          <div className="absolute bottom-20 left-1/4 opacity-20">
            <PhoneAnimation variant="ringing" size="sm" />
          </div>
          
          <div className="relative z-10 max-w-5xl mx-auto text-center w-full py-20">
            <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm text-teal px-5 py-2.5 rounded-full text-sm font-semibold mb-8 shadow-lg">
              <i className="ri-shield-check-line text-base"></i>
              <span>Trusted Partner Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight leading-tight">
              Partner with <span className="text-teal">PointCalls</span> <span className="text-white/80 text-3xl sm:text-4xl md:text-5xl">by instavisible</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed">
              Create a sustainable revenue stream for your professional community or business organization while providing members with cutting-edge AI phone technology. Earn recurring commissions for every active subscription.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="https://tidycal.com/mattv/15-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-teal text-white hover:bg-teal/90 text-base font-semibold px-8 h-14 rounded-xl shadow-lg shadow-teal/30 hover:shadow-teal/40 transition-all duration-300 hover:scale-105"
              >
                Apply for Partnership
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <button 
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-base font-semibold px-8 h-14 rounded-xl border-2 border-white/30 text-white hover:bg-white/10 transition-all duration-300"
              >
                How It Works
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-teal">20%+</div>
                <div className="text-sm text-white/80">Recurring Commission</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-teal">100%</div>
                <div className="text-sm text-white/80">Automated Tracking</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-teal">24/7</div>
                <div className="text-sm text-white/80">Partner Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership Benefits */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-6 tracking-tight">Partnership Benefits</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to build a successful AI phone technology community
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="p-8 bg-gradient-to-br from-teal/5 to-white border-2 border-gray-200 hover:border-teal transition-all duration-300 rounded-2xl group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Scalable Income</h3>
                <p className="text-gray-600 leading-relaxed">
                  Revenue grows with your community - the more members who subscribe through your referral, the more you earn month after month.
                </p>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-teal/10 to-white border-2 border-teal transition-all duration-300 rounded-2xl shadow-lg group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-customer-service-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Dedicated Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  24/7 support for all your referrals plus a dedicated partnership manager to help you maximize your earnings.
                </p>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-teal/5 to-white border-2 border-gray-200 hover:border-teal transition-all duration-300 rounded-2xl group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Build a Team</h3>
                <p className="text-gray-600 leading-relaxed">
                  Enroll other partners and earn a percentage of what they generate automatically. Grow your network, grow your income.
                </p>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-teal/5 to-white border-2 border-gray-200 hover:border-teal transition-all duration-300 rounded-2xl group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Premium Product</h3>
                <p className="text-gray-600 leading-relaxed">
                  Referrals get access to our full AI phone agent platform - answering, qualifying, and booking calls 24/7.
                </p>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-teal/5 to-white border-2 border-gray-200 hover:border-teal transition-all duration-300 rounded-2xl group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-dashboard-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Real-Time Dashboard</h3>
                <p className="text-gray-600 leading-relaxed">
                  Track your referrals, conversions, and earnings in real-time through your personalized partner dashboard.
                </p>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-teal/5 to-white border-2 border-gray-200 hover:border-teal transition-all duration-300 rounded-2xl group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-gift-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Marketing Resources</h3>
                <p className="text-gray-600 leading-relaxed">
                  Get access to co-branded materials, email templates, and promotional content to share with your audience.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Ideal Partner Organizations */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-6 tracking-tight">Ideal Partner Organizations</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We partner with <strong>professional communities</strong>, <strong>business associations</strong>, and organizations serving high-call-volume industries
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-navy mb-3">Professional Associations</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      Bar associations, real estate boards, contractor associations, and professional organizations
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>State Bar Associations</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Realtor Associations</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Contractor Networks</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-navy mb-3">Educational Communities</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      Business coaching programs, certification courses, and professional development groups
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Business Coaching Programs</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Sales Training Courses</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Continuing Education Providers</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-group-line text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-navy mb-3">Franchise Networks</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      Multi-location service businesses and franchise organizations
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Home Service Franchises</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Real Estate Brokerages</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Staffing Agency Networks</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-community-line text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-navy mb-3">Online Communities</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      Business forums, membership sites, and professional networks
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Industry-Specific Forums</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>Business Membership Sites</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal"></i>
                        <span>LinkedIn Groups</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-6">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Simple three-step process to start earning revenue from your professional community
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative group">
                <div className="bg-gradient-to-br from-teal/5 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-teal transition-all duration-500 hover:shadow-xl h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-user-add-line text-3xl text-white"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-5xl font-bold text-teal">01</span>
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">Apply & Get Approved</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Submit your application and get approved within 24-48 hours. Receive your unique tracking link and partner dashboard access.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="bg-gradient-to-br from-teal/5 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-teal transition-all duration-500 hover:shadow-xl h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-share-forward-line text-3xl text-white"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-5xl font-bold text-teal">02</span>
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">Share with Your Community</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Share your affiliate link with your community through email, newsletters, or member portal. We can even help you with outreach.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="bg-gradient-to-br from-teal/5 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-teal transition-all duration-500 hover:shadow-xl h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-5xl font-bold text-teal">03</span>
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">Earn Recurring Revenue</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Earn recurring commissions for every active subscriber. Track your performance in real-time through your partner dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-navy via-navy/95 to-navy/90 relative overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
          
          {/* Animated phone icons */}
          <div className="absolute top-10 left-10 opacity-10">
            <PhoneAnimation variant="floating" size="md" />
          </div>
          <div className="absolute bottom-10 right-10 opacity-10">
            <PhoneAnimation variant="pulse" size="md" />
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Ready to Create a New Revenue Stream?
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Join leading <strong>professional organizations</strong> and <strong>business communities</strong> earning sustainable revenue while providing exceptional value to their members
            </p>
            <a 
              href="https://tidycal.com/mattv/15-minute-meeting"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-teal text-white hover:bg-teal/90 text-lg font-semibold px-10 h-16 rounded-xl shadow-lg transition-all duration-300 hover:scale-105"
            >
              Apply for Partnership
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PartnershipsPage;
